import { ref, computed, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'

const API_BASE = 'https://sarvban.com/app/api/anar'

// Currency rates
const usdRate = ref<number | null>(null)
const iqdRate = ref<number | null>(null)
const rubRate = ref<number | null>(null)
const tryRate = ref<number | null>(null)
const currencyLoading = ref(false)
const currencyError = ref<string | null>(null)

const fetchCurrencyRates = async () => {
  currencyLoading.value = true
  currencyError.value = null
  try {
    const [usdResp, iqdResp, rubResp, tryResp] = await Promise.all([
      fetch(`${API_BASE}/get-currency/?flag=flag-us`),
      fetch(`${API_BASE}/get-currency/?flag=flag-iq`),
      fetch(`${API_BASE}/get-currency/?flag=flag-ru`),
      fetch(`${API_BASE}/get-currency/?flag=flag-tr`)
    ])
    if (usdResp.ok) { const d = await usdResp.json(); if (d.ok && d.price) usdRate.value = d.price }
    if (iqdResp.ok) { const d = await iqdResp.json(); if (d.ok && d.price) iqdRate.value = d.price }
    if (rubResp.ok) { const d = await rubResp.json(); if (d.ok && d.price) rubRate.value = d.price }
    if (tryResp.ok) { const d = await tryResp.json(); if (d.ok && d.price) tryRate.value = d.price }
  } catch (e) {
    console.error('Error fetching currency rates:', e)
    currencyError.value = 'خطا در دریافت نرخ ارز'
  } finally {
    currencyLoading.value = false
  }
}

// Check if product is exportable (info.value = 3358 for صادراتی)
const isProductExportable = (product: any) => {
  if (!product?.info || !Array.isArray(product.info)) return false
  return product.info.some(spec => spec.id === 3358 || spec.value === 3358 || spec.parent === 'صادراتی')
}

export const useCurrencyConversion = (product: any) => {
  const { locale: currentLocale } = useI18n()
  
  const isExportable = computed(() => isProductExportable(product))
  
  // For Persian (fa) locale, only show currency conversion for exportable products
  const shouldShowCurrency = computed(() => {
    const locale = currentLocale.value
    if (locale === 'fa') {
      return isExportable.value // Only exportable products show currency in fa
    }
    return true // All other locales always show currency
  })
  
  const localeRate = computed(() => {
    switch (currentLocale.value) {
      case 'en': return usdRate.value
      case 'ar': return iqdRate.value
      case 'ru': return rubRate.value
      case 'tr': return tryRate.value
      case 'fa': return usdRate.value || 1
      default: return ''
    }
  })

  const localeSymbol = computed(() => {
    switch (currentLocale.value) {
      case 'en': return '$'
      case 'ar': return 'د.ع'
      case 'ru': return '₽'
      case 'tr': return '₺'
      default: return 'تومان' // fa defaults to IQD symbol
    }
  })

  const localeCode = computed(() => {
    switch (currentLocale.value) {
      case 'en': return 'USD'
      case 'ar': return 'IQD'
      case 'ru': return 'RUB'
      case 'tr': return 'TRY'
      default: return 'IRR' // fa defaults to IQD
    }
  })

  const priceInLocaleCurrency = computed(() => {
    if (!shouldShowCurrency.value) return null
    const rate = localeRate.value
    if (!rate || !product?.price?.price) return null
    return Math.round(Number(product.price.price) * 10 / rate)
  })

  const formattedLocalePrice = computed(() => {
    if (!shouldShowCurrency.value) return null
    const price = priceInLocaleCurrency.value
    if (!price) return null
    return price.toLocaleString('fa-IR') + ' ' + localeSymbol.value
  })

  // Auto-fetch rates when composable is used
  onMounted(() => {
    fetchCurrencyRates()
  })

  // Also refetch when locale changes
  watch(() => currentLocale.value, () => {
    fetchCurrencyRates()
  })

  return {
    usdRate,
    iqdRate,
    rubRate,
    tryRate,
    currencyLoading,
    currencyError,
    fetchCurrencyRates,
    localeRate,
    localeSymbol,
    localeCode,
    priceInLocaleCurrency,
    formattedLocalePrice,
    shouldShowCurrency,
    isExportable
  }
}
